'use client';

import { useState } from 'react';

/**
 * VK-style media slider component
 * Displays media in a swipeable carousel like in real VK posts
 * Images are NOT cropped (object-contain)
 */
export function VkMediaSlider({ mediaFiles, onMediaClick }) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [imageErrors, setImageErrors] = useState(new Set());

  if (!mediaFiles || mediaFiles.length === 0) return null;

  const handleImageError = (index) => {
    setImageErrors((prev) => new Set(prev).add(index));
  };

  const goToNext = () => {
    setCurrentIndex((prev) => (prev + 1) % mediaFiles.length);
  };

  const goToPrev = () => {
    setCurrentIndex((prev) => (prev - 1 + mediaFiles.length) % mediaFiles.length);
  };

  const goToSlide = (index) => {
    setCurrentIndex(index);
  };

  const currentMedia = mediaFiles[currentIndex];
  const hasError = imageErrors.has(currentIndex);

  return (
    <div className="vk-media-slider w-full relative bg-black rounded-lg overflow-hidden">
      {/* Main Image/Video */}
      <div 
        className="relative w-full aspect-[4/3] flex items-center justify-center cursor-pointer"
        onClick={() => onMediaClick?.(currentIndex)}
      >
        {currentMedia.type === 'video' || hasError ? (
          <div className="flex items-center justify-center w-full h-full bg-gray-900">
            {currentMedia.type === 'video' ? (
              <div className="text-center">
                <div className="w-16 h-16 mx-auto mb-3 rounded-full bg-blue-500 flex items-center justify-center">
                  <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M8 5v14l11-7z" />
                  </svg>
                </div>
                <span className="text-white text-sm">Видео</span>
              </div>
            ) : (
              <div className="text-center">
                <svg className="w-12 h-12 mx-auto mb-2 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                <span className="text-gray-500 text-sm">Изображение</span>
              </div>
            )}
          </div>
        ) : (
          <img
            src={currentMedia.url}
            alt={`Фото ${currentIndex + 1}`}
            className="w-full h-full object-contain"
            onError={() => handleImageError(currentIndex)}
            loading="lazy"
          />
        )}
      </div>

      {/* Navigation Arrows (only if more than 1 image) */}
      {mediaFiles.length > 1 && (
        <>
          <button
            onClick={(e) => {
              e.stopPropagation();
              goToPrev();
            }}
            className="absolute left-2 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-white/90 hover:bg-white shadow-lg flex items-center justify-center transition-all z-10"
            aria-label="Предыдущее фото"
          >
            <svg className="w-5 h-5 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>

          <button
            onClick={(e) => {
              e.stopPropagation();
              goToNext();
            }}
            className="absolute right-2 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-white/90 hover:bg-white shadow-lg flex items-center justify-center transition-all z-10"
            aria-label="Следующее фото"
          >
            <svg className="w-5 h-5 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>

          {/* Counter */}
          <div className="absolute top-3 right-3 bg-black/60 text-white px-2.5 py-1 rounded-full text-xs font-medium">
            {currentIndex + 1} / {mediaFiles.length}
          </div>

          {/* Dots Indicator */}
          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
            {mediaFiles.map((_, index) => (
              <button
                key={index}
                onClick={(e) => {
                  e.stopPropagation();
                  goToSlide(index);
                }}
                className={`w-1.5 h-1.5 rounded-full transition-all ${
                  index === currentIndex
                    ? 'bg-white w-6'
                    : 'bg-white/50 hover:bg-white/75'
                }`}
                aria-label={`Перейти к фото ${index + 1}`}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

export default VkMediaSlider;
